import React, { memo, useState } from 'react';
import { Icon, Dropdown, Input, Text } from '@shootsta/common-react';
import { Shot, SCRIPT_TONE_OPTIONS, DragHandlers } from '../types';
import { customSelectStyles } from '../styles/dropdownStyles';
import { useSceneCardHandlers } from '../hooks/useSceneCardHandlers';
import CollapsibleTextInput from './CollapsibleTextInput';
import MediaSection from '../containers/MediaSection';

const TONE_OPTIONS = SCRIPT_TONE_OPTIONS.filter((opt) => opt.value !== '').map(
  (option) => ({
    label: (
      <div className="flex items-center gap-2 text-[#515E71]">
        <Icon
          name={option.icon}
          size={14}
          color="slateBlueGray"
          className="pl-1"
        />
        {option.label}
      </div>
    ),
    value: option.value
  })
);

interface ListViewProps {
  shot: Shot;
  sceneIndex: number;
  hasUpdatePermission: boolean;
  hasDeletePermission: boolean;
  isDragging: boolean;
  cardStyles: React.CSSProperties;
  onUpdateShot: (updates: Partial<Shot>) => void;
  onDeleteShot: () => void;
  onDuplicateShot: () => void;
  onSaveShot: () => Promise<void>;
  addFileToQueue: (payload: any, orgId: string) => Promise<string>;
  organisationId: string;
  dragHandlers?: DragHandlers;
  commentCount?: number;
  onComment?: () => void;
}

function ListView({
  shot,
  sceneIndex,
  hasUpdatePermission,
  hasDeletePermission,
  isDragging,
  cardStyles,
  onUpdateShot,
  onDeleteShot,
  onDuplicateShot,
  onSaveShot,
  addFileToQueue,
  organisationId,
  dragHandlers,
  commentCount,
  onComment
}: ListViewProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const {
    sceneId,
    handleFieldBlur,
    handleScriptToneChange,
    handleTitleBlur,
    handleAssetChange,
    handleDragStart
  } = useSceneCardHandlers({
    shot,
    onUpdateShot,
    onSaveShot,
    onDeleteShot,
    onDuplicateShot,
    hasDeletePermission,
    dragHandlers
  });

  const toneDropdownValue = TONE_OPTIONS.find(
    (option) => option.value === shot.scriptTone
  );

  return (
    <div
      ref={(cardRef) =>
        cardRef?.setAttribute('data-scene-card', String(sceneId))
      }
      className={`bg-white rounded-lg border border-russian overflow-hidden ${isDragging ? 'opacity-50' : ''}`}
      style={cardStyles}
    >
      {/* Header Row */}
      <div className="flex items-center gap-3 px-4 py-3 bg-sterling border-b border-russian">
        {hasUpdatePermission && dragHandlers && (
          <div
            className="cursor-grab hover:bg-russian rounded p-0.5"
            draggable
            onDragStart={handleDragStart}
            onDragEnd={dragHandlers.onDragEnd}
          >
            <Icon name="dragindicator" size={20} color="regent" />
          </div>
        )}
        <span className="flex h-7 w-7 items-center justify-center rounded bg-darkDenim text-sm font-semibold text-white flex-shrink-0">
          {sceneIndex + 1}
        </span>
        <div className="flex-1 min-w-0 shot-v3__title-input">
          <Input
            defaultValue={shot.name || ''}
            onBlur={handleTitleBlur}
            disabled={!hasUpdatePermission}
            placeholder={`Scene ${sceneIndex + 1}`}
            id={`title_list_${sceneIndex}`}
            className="h-8"
          />
        </div>
        <div className="flex items-center gap-4 pl-2">
          {hasUpdatePermission && (
            <>
              <Icon
                name="contentcopy"
                size={18}
                color="regent"
                onClick={onDuplicateShot}
              />
              {hasDeletePermission && (
                <Icon
                  onClick={onDeleteShot}
                  name="deleteicon"
                  size={18}
                  color="regent"
                />
              )}
            </>
          )}

          <Icon
            className="ml-3"
            onClick={() => setIsExpanded(!isExpanded)}
            name={isExpanded ? 'chevronup' : 'chevrondown'}
            size={18}
            color="regent"
          />
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="p-4 flex flex-col gap-4">
          <MediaSection
            assetId={shot.assetId || ''}
            addFileToQueue={addFileToQueue}
            organisationId={organisationId}
            hasUpdatePermission={hasUpdatePermission}
            hasDeletePermission={hasDeletePermission}
            onAssetChange={handleAssetChange}
            onSave={onSaveShot}
            viewMode="list"
            commentCount={commentCount}
            onComment={onComment}
          />

          {/* Script + Visual Description Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col">
              <div className="flex items-center justify-between mb-2 mt-2">
                <div className="flex items-center gap-1.5">
                  <Icon name="transcribe" size={16} />
                  <Text label noMargin>
                    Script
                  </Text>
                </div>
                <div className="flex items-center gap-1 ml-auto cursor-pointer [&_.icon-arrowdown_path]:fill-[#515e71]">
                  <Dropdown
                    onChange={handleScriptToneChange}
                    options={TONE_OPTIONS}
                    value={toneDropdownValue}
                    placeholder="Select tone"
                    disabled={!hasUpdatePermission}
                    selectStyles={customSelectStyles}
                  />
                </div>
              </div>
              <div className="flex-1 flex flex-col [&_.input-container--multiline]:!flex-1 [&_.input-container--multiline]:!items-baseline [&_.input-container--multiline>textarea]:!h-full [&_.input-container--multiline>textarea]:!resize-none">
                <Input
                  multiline
                  rows={3}
                  defaultValue={shot.script || ''}
                  placeholder="E.g. Take your business to the next level with a simple, powerful tool."
                  onBlur={handleFieldBlur('script')}
                  disabled={!hasUpdatePermission}
                  id={`script_list_${sceneIndex}`}
                />
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex items-center gap-1.5 mb-2 h-[32px]">
                <Icon name="inspiration" size={16} />
                <Text label noMargin>
                  Visual Description
                </Text>
              </div>
              <div className="flex-1 flex flex-col [&_.input-container--multiline]:!flex-1 [&_.input-container--multiline]:!items-baseline [&_.input-container--multiline>textarea]:!h-full [&_.input-container--multiline>textarea]:!resize-none">
                <Input
                  multiline
                  rows={3}
                  id={`script_list_${sceneIndex}`}
                  defaultValue={shot.description || ''}
                  placeholder="E.g. Shot of a user navigating the product, followed by a close-up of its key features."
                  onBlur={handleFieldBlur('description')}
                  disabled={!hasUpdatePermission}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(ListView);
